import { useCallback } from 'react';
import { useDatabaseConnection } from '@/contexts/DatabaseConnectionContext';
import { TableDescription } from '@/types';

export interface TableScanStatusResult {
  // Status data
  total: number;
  scanned: number;
  synchronizing: number;
  failed: number;
  hasUnscanned: boolean;
  loading: boolean;
  scanningInProgress: boolean;
  progressPercentage: number;

  // Table data
  tables: TableDescription[];
  scannedTables: TableDescription[];
  unscannedTables: TableDescription[];
  failedTables: TableDescription[];

  // Actions
  refresh: () => Promise<void>;
  rescanAll: () => Promise<void>;
  rescanFailed: () => Promise<void>;
  rescanSpecific: (tableNames: string[]) => Promise<void>;

  // Computed states
  isComplete: boolean;
  hasErrors: boolean;
  statusMessage: string;
}

/**
 * Centralized hook for managing table scan status across components
 * Replaces all duplicate checkTableStatus functions
 */
export function useTableScanStatus(connectionId?: string): TableScanStatusResult {
  const {
    selectedConnectionId,
    tableDescriptions,
    tableScanStatus,
    scanningInProgress,
    getTableScanStatus,
    triggerTableScan,
    refreshTableStatus,
  } = useDatabaseConnection();

  const activeConnectionId = connectionId || selectedConnectionId;
  const tables = tableDescriptions[activeConnectionId] || [];
  const status = getTableScanStatus(activeConnectionId);
  const isScanning = scanningInProgress[activeConnectionId] || false;

  // Computed values
  const scannedTables = tables.filter(t => t.status === 'SCANNED');
  const unscannedTables = tables.filter(t => t.status !== 'SCANNED');
  const failedTables = tables.filter(t => t.status === 'FAILED');
  const synchronizingTables = tables.filter(t => t.status === 'SYNCHRONIZING');

  const progressPercentage = status.total > 0 ? Math.round((status.scanned / status.total) * 100) : 0;
  const isComplete = status.total > 0 && status.scanned === status.total;
  const hasErrors = status.failed > 0;

  // Generate status message
  const getStatusMessage = useCallback((): string => {
    if (status.loading) return 'Loading table information...';
    if (status.total === 0) return 'No tables found';
    if (isScanning) return `Scanning ${status.synchronizing} tables...`;
    if (isComplete) return `All ${status.total} tables scanned successfully`;
    if (hasErrors) return `${status.scanned}/${status.total} tables scanned, ${status.failed} failed`;
    return `${status.scanned}/${status.total} tables scanned`;
  }, [status, isScanning, isComplete, hasErrors]);

  const statusMessage = getStatusMessage();

  // Actions
  const refresh = useCallback(async () => {
    if (!activeConnectionId) return;
    await refreshTableStatus(activeConnectionId);
  }, [activeConnectionId, refreshTableStatus]);

  const rescanAll = useCallback(async () => {
    if (!activeConnectionId) return;
    await triggerTableScan(activeConnectionId);
  }, [activeConnectionId, triggerTableScan]);

  const rescanFailed = useCallback(async () => {
    if (!activeConnectionId || failedTables.length === 0) return;
    const failedTableNames = failedTables.map(t => t.table_name);
    await triggerTableScan(activeConnectionId, failedTableNames);
  }, [activeConnectionId, failedTables, triggerTableScan]);

  const rescanSpecific = useCallback(async (tableNames: string[]) => {
    if (!activeConnectionId || tableNames.length === 0) return;
    await triggerTableScan(activeConnectionId, tableNames);
  }, [activeConnectionId, triggerTableScan]);

  // Note: Auto-loading is now handled by DatabaseConnectionContext when connection is selected
  // This prevents duplicate API calls from multiple components using this hook

  return {
    // Status data
    total: status.total,
    scanned: status.scanned,
    synchronizing: status.synchronizing,
    failed: status.failed,
    hasUnscanned: status.hasUnscanned,
    loading: status.loading,
    scanningInProgress: isScanning,
    progressPercentage,

    // Table data
    tables,
    scannedTables,
    unscannedTables,
    failedTables,

    // Actions
    refresh,
    rescanAll,
    rescanFailed,
    rescanSpecific,

    // Computed states
    isComplete,
    hasErrors,
    statusMessage,
  };
}

/**
 * Simplified hook for components that only need basic scan status
 */
export function useTableScanSummary(connectionId?: string) {
  const {
    total,
    scanned,
    hasUnscanned,
    loading,
    progressPercentage,
    statusMessage,
    isComplete
  } = useTableScanStatus(connectionId);

  return {
    total,
    scanned,
    hasUnscanned,
    loading,
    progressPercentage,
    statusMessage,
    isComplete,
    displayText: `${scanned}/${total} tables ready`
  };
}